import React, { useEffect, useState } from "react";

import {
  listCustomDeployments,
  requestCustomVerification,
  checkCustomVerificationStatus,
} from "../../lib/customContractsApi";

function safeError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    err?.shortMessage ||
    fallback
  );
}

function shortAddress(value) {
  const clean = String(value || "").trim();
  if (clean.length <= 14) return clean || "-";
  return `${clean.slice(0, 8)}…${clean.slice(-6)}`;
}

function getDeploymentKey(item) {
  return item?.deploymentFile || item?.txHash || item?.contractAddress || item?.address;
}

function normalizeVerificationStatus(item) {
  const rawStatus = String(
    item?.verificationStatus ||
      item?.verification?.status ||
      "not_requested",
  )
    .trim()
    .toLowerCase();

  const text = [
    item?.verificationMessage,
    item?.verificationFailureReason,
    item?.verification?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    rawStatus === "verified" ||
    text.includes("already verified") ||
    text.includes("pass - verified") ||
    text.includes("successfully verified")
  ) {
    return "verified";
  }

  if (rawStatus === "submitted" || rawStatus === "submitting") {
    return "submitted";
  }

  if (rawStatus === "failed") {
    return "failed";
  }

  return rawStatus || "not_requested";
}

export default function CustomDeploymentHistory({ isAuthed }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [workingKey, setWorkingKey] = useState("");
  const [statusByKey, setStatusByKey] = useState({});

  async function loadHistory() {
    setError("");

    if (!isAuthed) {
      setItems([]);
      return;
    }

    setLoading(true);

    try {
      const result = await listCustomDeployments({
        limit: 20,
      });

      const list =
        result?.deployments ||
        result?.items ||
        result?.records ||
        result?.data ||
        [];

      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(safeError(err, "Failed to load custom deployments."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  async function handleVerify(item) {
    const key = getDeploymentKey(item);

    setWorkingKey(key);
    setStatusByKey((prev) => ({
      ...prev,
      [key]: {
        type: "info",
        message: "Submitting verification…",
        raw: null,
      },
    }));

    try {
      const result = await requestCustomVerification({
        deploymentFile: item.deploymentFile || null,
        requestId: item.requestId || null,
        txHash: item.txHash || null,
      });

      setStatusByKey((prev) => ({
        ...prev,
        [key]: {
          type: result?.ok ? "success" : "error",
          message:
            result?.verification?.message ||
            result?.message ||
            result?.error ||
            (result?.ok
              ? "Verification submitted. Check status shortly."
              : "Verification request failed."),
          raw: result,
        },
      }));

      await loadHistory();
    } catch (err) {
      setStatusByKey((prev) => ({
        ...prev,
        [key]: {
          type: "error",
          message: safeError(err, "Verification request failed."),
          raw: null,
        },
      }));
    } finally {
      setWorkingKey("");
    }
  }

  async function handleCheckStatus(item) {
    const key = getDeploymentKey(item);

    setWorkingKey(key);
    setStatusByKey((prev) => ({
      ...prev,
      [key]: {
        type: "info",
        message: "Checking verification status…",
        raw: null,
      },
    }));

    try {
      const result = await checkCustomVerificationStatus({
        deploymentFile: item.deploymentFile || null,
        requestId: item.requestId || null,
        txHash: item.txHash || null,
      });

      setStatusByKey((prev) => ({
        ...prev,
        [key]: {
          type: result?.ok ? "success" : "error",
          message:
            result?.verification?.message ||
            result?.message ||
            result?.error ||
            result?.verification?.status ||
            "Verification status updated.",
          raw: result,
        },
      }));

      await loadHistory();
    } catch (err) {
      setStatusByKey((prev) => ({
        ...prev,
        [key]: {
          type: "error",
          message: safeError(err, "Verification status check failed."),
          raw: null,
        },
      }));
    } finally {
      setWorkingKey("");
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Custom Deployment History
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Saved custom deployments from your workspace. Use this if you leave
            the builder after deployment.
          </p>
        </div>

        <button
          type="button"
          onClick={loadHistory}
          disabled={loading || !isAuthed}
          className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {!isAuthed ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
          Connect wallet and sign in from the top bar to view your custom deployments.
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {isAuthed && !loading && items.length === 0 ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          No custom deployments found yet.
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {items.map((item) => {
          const key = getDeploymentKey(item);
          const contractAddress = item.contractAddress || item.address || "";
          const verificationStatus = normalizeVerificationStatus(item);
          const status = statusByKey[key];
          const isWorking = workingKey === key;

          return (
            <div
              key={key}
              className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4"
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-slate-950">
                      {item.contractName || "Custom Contract"}
                    </h3>

                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {item.tokenType || "CUSTOM"}
                    </span>

                    <span
                      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                        verificationStatus === "verified"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : verificationStatus === "failed"
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {verificationStatus}
                    </span>
                  </div>

                  <div className="mt-2 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                    <div>
                      <span className="font-semibold text-slate-800">Network:</span>{" "}
                      {item.network || "-"} / Chain {item.chainId || "-"}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-800">Contract:</span>{" "}
                      {shortAddress(contractAddress)}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="font-semibold text-slate-800">TX:</span>{" "}
                      {shortAddress(item.txHash)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
                  {item.explorerUrl ? (
                    <a
                      href={item.explorerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-center text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Explorer
                    </a>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => handleVerify(item)}
                    disabled={isWorking || verificationStatus === "verified"}
                    className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                  >
                    {verificationStatus === "verified"
                      ? "Verified"
                      : isWorking
                        ? "Working…"
                        : "Verify"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCheckStatus(item)}
                    disabled={isWorking || !item.verificationGuid}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Check Status
                  </button>
                </div>
              </div>

              {status ? (
                <div
                  className={`mt-3 rounded-xl border p-3 text-xs font-medium ${
                    status.type === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : status.type === "error"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-blue-200 bg-blue-50 text-blue-800"
                  }`}
                >
                  {status.message}

                  {status.raw ? (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-semibold">
                        View response
                      </summary>
                      <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-slate-950 p-3 text-slate-100">
                        {JSON.stringify(status.raw, null, 2)}
                      </pre>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}